import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { CustomerTable } from '../CustomerTable'
import { Customer } from '../../types/search.types'
import './CustomerSearch.styles.css'

interface CustomerSearchProps {
  customers: Customer[]
  loading: boolean
  error: string | null
  searchQuery: string
  hasSearched: boolean
  onSearch: (query: string) => void
  onSelectCustomer: (customer: Customer) => void
}

export const CustomerSearch: React.FC<CustomerSearchProps> = ({
  customers,
  loading,
  error,
  searchQuery,
  hasSearched,
  onSearch,
  onSelectCustomer
}) => {
  return (
    <>
      <div className="search-input-section">
        <div className="search-input-wrapper">
          <svg className="search-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            id="customer-search"
            type="text"
            className="search-input-field"
            placeholder="Rechercher par email, téléphone, prénom ou nom de famille..."
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="search-input-clear"
              onClick={() => onSearch('')}
              aria-label="Clear search"
            >
              <XMarkIcon className="search-clear-icon" />
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="search-results-section">
          <div className="search-loading-state">
            <div className="search-loading-spinner"></div>
            <p className="search-loading-text">Recherche en cours...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="search-error-state">
          <p className="search-error-message">⚠️ Erreur: {error}</p>
        </div>
      )}

      {!hasSearched && !loading && searchQuery.length < 2 && (
        <div className="search-results-section">
          <div className="search-empty-state">
            <div className="search-empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <h3 className="search-empty-title">Recherchez vos clients</h3>
            <p className="search-empty-description">
              Commencez à taper dans le champ de recherche pour trouver des clients par email, téléphone, prénom ou nom
            </p>
          </div>
        </div>
      )}

      {hasSearched && customers.length === 0 && !loading && (
        <div className="search-results-section">
          <div className="search-empty-state">
            <div className="search-empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
                <path d="M8 11h6"/>
              </svg>
            </div>
            <h3 className="search-empty-title">Aucun résultat trouvé</h3>
            <p className="search-empty-description">
              Aucun client ne correspond à votre recherche "{searchQuery}"
            </p>
          </div>
        </div>
      )}

      {customers.length > 0 && (
        <div className="search-results-section">
          <div className="search-results-header">
            <h2 className="search-results-title">Résultats de recherche</h2>
            <span className="search-results-count">
              {customers.length} client{customers.length > 1 ? 's' : ''} trouvé{customers.length > 1 ? 's' : ''}
            </span>
          </div>
          
          <CustomerTable 
            customers={customers}
            onSelectCustomer={onSelectCustomer}
          />
        </div>
      )}
    </>
  )
}